import * as z from 'zod'

import {
  COUPON_PAGE_SIZES,
  COUPON_SORT_DIRS,
  COUPON_SORT_FIELDS,
  type CouponListFilters,
  DEFAULT_COUPON_PAGE_SIZE,
  DEFAULT_COUPON_SORT,
  parseCouponTagFilter,
  UNTAGGED_TAG_VALUE,
} from '@/domain/coupon-filters'
import { COUPON_STATUSES } from '@/domain/coupon-status'

/**
 * URL search-param schema for /admin/partners/$partnerId.
 *
 * The active tab is the page's main URL state; "overview" is the implicit
 * default and is NOT serialized, so the canonical URL stays clean.
 *
 * `origin` records how the admin reached this detail. Only the partners LIST
 * sets `origin=partners`; it tells the "Back to partners" control it may step
 * back in history to restore the exact list the admin left (page, filters,
 * sort, and scroll). Every other entry point (ledger, dashboard, orders, a
 * shared/deep link) omits it, so the control falls back to a plain link to the
 * list — the destination its label promises. See the identity rail. (Named
 * `origin`, not `from`, to avoid colliding with the LIST route's `from`
 * date-range filter in partners-search-params.ts.)
 *
 * Everything below `origin` is the Links tab's view — its tag scope, status,
 * batch, code search, sort and page. Each one is a SERVER query param, so the
 * view belongs to the shareable URL rather than to component state: a refresh, a
 * bookmark and a shared link all land on the same page of the same cut. Only the
 * Links tab reads them, and one tab is active at a time, so they carry no
 * per-tab prefix.
 */
export const PARTNER_DETAIL_TABS = [
  'overview',
  'report',
  'coupons',
  'campaigns',
  'commissions',
  'payouts',
] as const
export type PartnerDetailTab = (typeof PARTNER_DETAIL_TABS)[number]

// Every Links param is caught per field rather than leaning on the route's
// whole-object `.catch`: a stale bookmark naming a deleted tag (or a page size
// the grid no longer offers) should lose that one filter, not silently reset the
// tab and every other filter along with it.
export const partnerDetailSearchSchema = z.object({
  tab: z.enum(PARTNER_DETAIL_TABS).optional(),
  origin: z.literal('partners').optional(),
  /** A tag id, or `untagged` for the no-tags bucket. Absent means every tag. */
  tag: z
    .union([z.literal(UNTAGGED_TAG_VALUE), z.uuid()])
    .optional()
    .catch(undefined),
  status: z.enum(COUPON_STATUSES).optional().catch(undefined),
  batch: z.uuid().optional().catch(undefined),
  /** Case-insensitive substring match on the code. */
  code: z.string().optional().catch(undefined),
  page: z.coerce.number().int().positive().optional().catch(undefined),
  size: z.coerce
    .number()
    .int()
    .refine((value) => COUPON_PAGE_SIZES.some((size) => size === value))
    .optional()
    .catch(undefined),
  sort: z.enum(COUPON_SORT_FIELDS).optional().catch(undefined),
  dir: z.enum(COUPON_SORT_DIRS).optional().catch(undefined),
})

export type PartnerDetailSearch = z.infer<typeof partnerDetailSearchSchema>

export const DEFAULT_PARTNER_DETAIL_SEARCH: PartnerDetailSearch = {
  tab: undefined,
  origin: undefined,
  tag: undefined,
  status: undefined,
  batch: undefined,
  code: undefined,
  page: undefined,
  size: undefined,
  sort: undefined,
  dir: undefined,
}

/** The Links tab's slice of the URL — what the tab renders and writes back. */
export type CouponListSearch = Pick<
  PartnerDetailSearch,
  'tag' | 'status' | 'batch' | 'code' | 'page' | 'size' | 'sort' | 'dir'
>

/**
 * Translate the validated URL state into the request-side view. Absent params
 * resolve to the endpoint's own defaults (page 1, `-created_at`), so the
 * canonical URL carries nothing and still describes exactly what the server
 * returns.
 */
export const toCouponListFilters = (search: CouponListSearch): CouponListFilters => ({
  page: search.page ?? 1,
  pageSize: search.size ?? DEFAULT_COUPON_PAGE_SIZE,
  search: search.code ?? '',
  status: search.status ?? 'all',
  batchId: search.batch ?? '',
  tagFilter: parseCouponTagFilter(search.tag ?? ''),
  sort: {
    field: search.sort ?? DEFAULT_COUPON_SORT.field,
    dir: search.dir ?? DEFAULT_COUPON_SORT.dir,
  },
})
